import React, { useState } from 'react';
import { styled, Theme, useMediaQuery, Slide } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import useScrollTrigger from '@mui/material/useScrollTrigger';

import PopUpContactUs from '../../ContactUs/PopUpContactUs';

import Navigation from '../Navigation';

import { useHeader } from './HeaderProvider';
import Logo from './Logo';
import BurgerMenu from '../BurgerMenu';
import { useRouter } from 'next/router';
import classnames from 'classnames';

export const HEADER_TRANSITION = {
  duration: 200,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

export const HEADER_HEIGHT = {
  desktop: 49,
  tablet: 65,
  mobile: 62,
};

const StyledAppBar = styled(AppBar)`
  background-color: transparent;
  box-shadow: none;
`;

const StyledToolbar = styled(Toolbar)`
  min-height: 0 !important;
  padding: 0 !important;
  background-color: #fff;
  width: 100%;
  border-bottom: 1px solid #d2cfca;
`;

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll({ children }: HideOnScrollProps) {
  const trigger = useScrollTrigger();

  return (
    <Slide
      appear={false}
      direction="down"
      in={!trigger}
      timeout={HEADER_TRANSITION.duration}
    >
      {children}
    </Slide>
  );
}

interface HeaderProps {
  children: React.ReactNode;
  heightClass?: string;
  hideOnScroll?: boolean;
}

export const HeaderWrapper: React.FC<HeaderProps> = ({
  children,
  heightClass,
  hideOnScroll = false,
}) => {
  const header = (
    <StyledAppBar position="sticky">
      <div className={`w-full ${heightClass}`}>
        <StyledToolbar>{children}</StyledToolbar>
      </div>
    </StyledAppBar>
  );

  return hideOnScroll ? <HideOnScroll>{header}</HideOnScroll> : header;
};

// Create a stable component that doesn't depend on changing props
// Fixes the issue with the remounting of the component when the user searches
const StableDesktopMenu = React.memo(() => {
  const { fetchMoreBuildings, fetchMoreNhds, data } = useHeader();
  const [menuActive, setMenuActive] = useState(false);

  return (
    <React.Fragment>
      <div className="flex items-center justify-between w-full lg:min-w-min">
        <Logo />
        <Navigation
          isDesktop
          hidden={'hidden xl:flex'}
          active={menuActive}
          setActive={setMenuActive}
          dataSet={{
            fetchMoreBuildings,
            fetchMoreNhds,
            data,
          }}
        />
        <PopUpContactUs />
      </div>
    </React.Fragment>
  );
});
StableDesktopMenu.displayName = 'StableDesktopMenu';

const MobileMenu = () => {
  const { fetchMoreBuildings, fetchMoreNhds, data } = useHeader();
  const [menuActive, setMenuActive] = useState(false);

  return (
    <React.Fragment>
      <div className="flex items-center">
        <BurgerMenu
          active={menuActive}
          setActive={setMenuActive}
          dataSet={{
            fetchMoreBuildings,
            fetchMoreNhds,
            data,
          }}
        />
        <Logo />
      </div>
      <PopUpContactUs isMobile={true} />
    </React.Fragment>
  );
};

export const DefaultHeaderBody: React.FC = () => {
  const { asPath } = useRouter();
  const isHomePage = asPath === '/';
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));

  return (
    <div
      className={classnames(
        `
          flex max-w-full w-full items-center justify-between px-0 
          pl-[30px] md:pl-[45px] lg:pl-[50px] 
          h-[61px] md:h-16 lg:h-13 xl:h-[49px]
        `,
        {
          'container-header': isHomePage,
          container: !isHomePage,
        }
      )}
    >
      {isDesktop ? <StableDesktopMenu /> : <MobileMenu />}
    </div>
  );
};

const Header: React.FC<{ hideOnScroll?: boolean }> = ({
  hideOnScroll = false,
}) => {
  return (
    <HeaderWrapper hideOnScroll={hideOnScroll}>
      <DefaultHeaderBody />
    </HeaderWrapper>
  );
};

export default Header;
